<script lang="ts">
  /**
   * Смуга прогресу. Під час перетягування показує, куди потрапить відео, а
   * перемотує один раз — коли палець відпущено: кожен стрибок у HLS-потоці це
   * нове завантаження сегмента.
   */
  import { formatTime } from '../../lib/player/settings';

  interface Props {
    currentTime: number;
    duration: number;
    /** Секунда, до якої потік уже завантажено. */
    buffered: number;
    /** Плеєр повернуто на 90° (повний екран на телефоні): вісь смуги — вертикаль екрана. */
    rotated: boolean;
    onSeek: (sec: number) => void;
    /** Перетягування почалось/закінчилось — панель не має ховатися посеред нього. */
    onDragChange: (dragging: boolean) => void;
  }
  let { currentTime, duration, buffered, rotated, onSeek, onDragChange }: Props = $props();

  let trackEl = $state<HTMLDivElement | undefined>(undefined);
  let dragFraction = $state<number | null>(null);

  const fraction = $derived(
    dragFraction ?? (duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0),
  );
  const bufferedFraction = $derived(duration > 0 ? Math.min(1, buffered / duration) : 0);

  function fractionAt(e: PointerEvent): number {
    if (!trackEl) return 0;
    const r = trackEl.getBoundingClientRect();
    // rotate(90deg): «праворуч» у плеєрі — це «вниз» на екрані.
    const f = rotated ? (e.clientY - r.top) / r.height : (e.clientX - r.left) / r.width;
    return Math.min(1, Math.max(0, f));
  }

  function onDown(e: PointerEvent): void {
    if (duration <= 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragFraction = fractionAt(e);
    onDragChange(true);
  }
  function onMove(e: PointerEvent): void {
    if (dragFraction !== null) dragFraction = fractionAt(e);
  }
  function onUp(): void {
    if (dragFraction === null) return;
    onSeek(dragFraction * duration);
    dragFraction = null;
    onDragChange(false);
  }
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'ArrowLeft') onSeek(Math.max(0, currentTime - 5));
    else if (e.key === 'ArrowRight') onSeek(Math.min(duration, currentTime + 5));
    else return;
    e.preventDefault();
    e.stopPropagation();
  }
</script>

<div
  class="progress"
  class:dragging={dragFraction !== null}
  role="slider"
  tabindex="0"
  aria-label="Позиція відео"
  aria-valuemin={0}
  aria-valuemax={Math.floor(duration)}
  aria-valuenow={Math.floor(fraction * duration)}
  aria-valuetext={formatTime(fraction * duration)}
  onpointerdown={onDown}
  onpointermove={onMove}
  onpointerup={onUp}
  onpointercancel={onUp}
  onkeydown={onKey}
>
  <div class="track" bind:this={trackEl}>
    <div class="buffered" style:width="{bufferedFraction * 100}%"></div>
    <div class="played" style:width="{fraction * 100}%"></div>
    <div class="thumb" style:left="{fraction * 100}%"></div>
    {#if dragFraction !== null}
      <div class="tip" style:left="{fraction * 100}%">{formatTime(dragFraction * duration)}</div>
    {/if}
  </div>
</div>

<style>
  /* Висока зона дотику навколо тонкої смуги. */
  .progress {
    padding: 10px 0;
    cursor: pointer;
    touch-action: none;
    outline: none;
  }
  .track {
    position: relative;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.25);
  }
  .buffered,
  .played {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    border-radius: 2px;
  }
  .buffered {
    background: rgba(255, 255, 255, 0.35);
  }
  .played {
    background: var(--player-accent);
  }
  .thumb {
    position: absolute;
    top: 50%;
    width: 12px;
    height: 12px;
    margin: -6px 0 0 -6px;
    border-radius: 50%;
    background: var(--player-accent);
    transition: transform 0.12s;
  }
  .progress.dragging .thumb,
  .progress:focus-visible .thumb {
    transform: scale(1.5);
  }
  .tip {
    position: absolute;
    bottom: 16px;
    transform: translateX(-50%);
    padding: 3px 7px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    pointer-events: none;
  }
</style>
